import React, { useEffect, useState } from 'react';
import { Form, Spinner, Button, Modal } from 'react-bootstrap';
import axios from 'axios';
import toaster from '../../../../Utils/toaster';
import { useAuth } from '../../../../app/modules/auth';
import './MeetingRoomsSection.css';

interface RecordingTranscriptionStatus {
  isRecording?: boolean;
  isTranscription?: boolean;
  askPreCallQA?: boolean;
  isGenderIVR?: boolean;
  isForwardToOperator?: boolean;
  isRouteToBackstopPartner?: boolean;
  isRouteToBigWord?: boolean;
  isRouteToEffectiff?: boolean;
}

const API_URL = process.env.REACT_APP_API_URL

interface SettingsSectionProps {
  title: string;
  description: string;
  iconClass: string;
  children: React.ReactNode;
}

function SettingsSection({ title, description, iconClass, children }: SettingsSectionProps) {
  return (
    <div className="ipi-settings-section p-4 p-md-5">
      <div className="row g-4">
        <div className="col-md-4">
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className={`bi ${iconClass} text-muted fs-5`} aria-hidden="true" />
            <h5 className="fw-bold text-gray-900 mb-0">{title}</h5>
          </div>
          <p className="text-muted small mb-0 lh-base">{description}</p>
        </div>
        <div className="col-md-8 d-flex flex-column gap-3">{children}</div>
      </div>
    </div>
  );
}

interface SettingRowProps {
  label: string;
  description?: string;
  control: React.ReactNode;
  children?: React.ReactNode;
}

function SettingRow({ label, description, control, children }: SettingRowProps) {
  return (
    <div className="ipi-setting-row d-flex flex-column flex-sm-row align-items-sm-start justify-content-between gap-3 p-3 rounded border border-gray-200 bg-light">
      <div className="flex-grow-1">
        <div className="fw-semibold text-gray-900">{label}</div>
        {description && <div className="text-muted small mt-1">{description}</div>}
        {children && <div className="mt-3">{children}</div>}
      </div>
      <div className="flex-shrink-0">{control}</div>
    </div>
  );
}

export function OPISettingsForm({ customerId }: { customerId: number }) {
  const {currentUser} = useAuth()
  
  const [loading, setLoading] = useState<boolean>(true);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [callRecording, setCallRecording] = useState<boolean>(false);
  const [callTranscription, setCallTranscription] = useState<boolean>(false);
  const [askPreCallQA, setAskPreCallQA] = useState<boolean>(false);
  const [isGenderIVR, setIsGenderIVR] = useState<boolean>(false);
  const [isForwardToOperator, setIsForwardToOperator] = useState<boolean>(true);
  const [isRouteToBackstopPartner, setIsRouteToBackstopPartner] = useState<boolean>(true);
  const [isRouteToBigWord, setIsRouteToBigWord] = useState<boolean>(true);
  const [isRouteToEffectiff, setIsRouteToEffectiff] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  
  // Store initial values to detect changes
  const [initialRecording, setInitialRecording] = useState<boolean>(false);
  const [initialTranscription, setInitialTranscription] = useState<boolean>(false);
  const [initialAskPreCallQA, setInitialAskPreCallQA] = useState<boolean>(false);
  const [initialIsGenderIVR, setInitialIsGenderIVR] = useState<boolean>(false);
  const [initialIsForwardToOperator, setInitialIsForwardToOperator] = useState<boolean>(true);
  const [initialIsRouteToBackstopPartner, setInitialIsRouteToBackstopPartner] = useState<boolean>(true);
  const [initialIsRouteToBigWord, setInitialIsRouteToBigWord] = useState<boolean>(true);
  const [initialIsRouteToEffectiff, setInitialIsRouteToEffectiff] = useState<boolean>(true);

  // First fetch account details to get the id
  useEffect(() => {
    const fetchAccountDetails = async () => {
      if (!currentUser?.result?.code) {
        console.warn('No user code found to fetch account details');
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/accounts/GetAccountDetailByID/${currentUser.result.code}`);
        const accountData = response.data?.data || response.data;
        const id = accountData?.id || accountData?.code || null;
        
        if (id) {
          setAccountId(id);
          // After getting account id, fetch recording/transcription status
          fetchRecordingTranscriptionStatus();
        } else {
          console.warn('No id found in account details');
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Failed to fetch account details:', err);
        toaster('error', err?.response?.data?.message || err?.message || 'Failed to load account details');
        setLoading(false);
      }
    };

    const fetchRecordingTranscriptionStatus = async () => {
      if (!customerId) {
        console.warn('No customerId available to fetch recording/transcription status');
        setLoading(false);
        return;
      }
      
      try {
        const res = await axios.get(`${API_URL}/customer/recording-transcription-status`, {
          params: { customerId: customerId }
        });
        const data: RecordingTranscriptionStatus = res.data?.data || res.data;
        
        const recordingStatus = data.isRecording ?? false;
        const transcriptionStatus = data.isTranscription ?? false;
        const askPreCallQAStatus = data.askPreCallQA ?? false;
        const genderIVRStatus = data.isGenderIVR ?? false;
        const forwardToOperatorStatus = data.isForwardToOperator ?? true;
        const routeToBackstopPartnerStatus = data.isRouteToBackstopPartner ?? true;
        const routeToBigWordStatus = data.isRouteToBigWord ?? true;
        const routeToEffectiffStatus = data.isRouteToEffectiff ?? true;
        
        setCallRecording(recordingStatus);
        // If call recording is off, transcription must be off too
        setCallTranscription(transcriptionStatus && recordingStatus);
        setAskPreCallQA(askPreCallQAStatus);
        setIsGenderIVR(genderIVRStatus);
        setIsForwardToOperator(forwardToOperatorStatus);
        setIsRouteToBackstopPartner(routeToBackstopPartnerStatus);
        setIsRouteToBigWord(routeToBigWordStatus);
        setIsRouteToEffectiff(routeToEffectiffStatus);
        // Store initial values
        setInitialRecording(recordingStatus);
        setInitialTranscription(transcriptionStatus && recordingStatus);
        setInitialAskPreCallQA(askPreCallQAStatus);
        setInitialIsGenderIVR(genderIVRStatus);
        setInitialIsForwardToOperator(forwardToOperatorStatus);
        setInitialIsRouteToBackstopPartner(routeToBackstopPartnerStatus);
        setInitialIsRouteToBigWord(routeToBigWordStatus);
        setInitialIsRouteToEffectiff(routeToEffectiffStatus);
      } catch (err: any) {
        console.error('Failed to fetch recording/transcription status:', err);
        toaster('error', err?.response?.data?.message || err?.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    fetchAccountDetails();
  }, [currentUser?.result?.code]);

  // Check if there are changes
  useEffect(() => {
    const hasChanges = callRecording !== initialRecording
      || callTranscription !== initialTranscription
      || askPreCallQA !== initialAskPreCallQA
      || isGenderIVR !== initialIsGenderIVR
      || isForwardToOperator !== initialIsForwardToOperator
      || isRouteToBackstopPartner !== initialIsRouteToBackstopPartner
      || isRouteToBigWord !== initialIsRouteToBigWord
      || isRouteToEffectiff !== initialIsRouteToEffectiff;
    setHasChanges(hasChanges);
  }, [
    callRecording,
    callTranscription,
    askPreCallQA,
    isGenderIVR,
    isForwardToOperator,
    isRouteToBackstopPartner,
    isRouteToBigWord,
    isRouteToEffectiff,
    initialRecording,
    initialTranscription,
    initialAskPreCallQA,
    initialIsGenderIVR,
    initialIsForwardToOperator,
    initialIsRouteToBackstopPartner,
    initialIsRouteToBigWord,
    initialIsRouteToEffectiff,
  ]);

  // Handle recording toggle change (local state only, no API call)
  const handleRecordingChange = (isRecording: boolean) => {
    setCallRecording(isRecording);
    // If turning off recording, also turn off transcription
    if (!isRecording) {
      setCallTranscription(false);
    }
  };

  // Handle transcription toggle change (local state only, no API call)
  const handleTranscriptionChange = (isTranscription: boolean) => {
    // Transcription can only be enabled if recording is enabled
    if (isTranscription && !callRecording) {
      toaster('error', 'Call recording must be enabled before enabling transcription');
      return;
    }
    setCallTranscription(isTranscription);
  };

  // Save settings with confirmation dialog
  const handleSave = () => {
    setShowConfirmModal(true);
  };

  // Update both recording and transcription status using combined API
  const updateRecordingTranscriptionStatus = async (appliedToChild: boolean) => {
    if (!customerId) {
      toaster('error', 'Customer ID not found. Please refresh and try again.');
      return;
    }
    
    const effectiveRouteToBackstopPartner =
      isRouteToBackstopPartner && (isRouteToBigWord || isRouteToEffectiff);

    try {
      setIsSaving(true);
      const response = await axios.post(
        `${API_URL}/customer/update-recording-transcription-status`,
        {
          customerId: customerId,
          isRecording: callRecording,
          isTranscription: callTranscription,
          askPreCallQA: askPreCallQA,
          isGenderIVR: isGenderIVR,
          isForwardToOperator: isForwardToOperator,
          isRouteToBackstopPartner: effectiveRouteToBackstopPartner,
          isRouteToBigWord: isRouteToBigWord,
          isRouteToEffectiff: isRouteToEffectiff,
          appliedToChild: appliedToChild
        }
      );

      if (response.data.status === 'S') {
        // Update initial values to reflect saved state
        setIsRouteToBackstopPartner(effectiveRouteToBackstopPartner);
        setInitialRecording(callRecording);
        setInitialTranscription(callTranscription);
        setInitialAskPreCallQA(askPreCallQA);
        setInitialIsGenderIVR(isGenderIVR);
        setInitialIsForwardToOperator(isForwardToOperator);
        setInitialIsRouteToBackstopPartner(effectiveRouteToBackstopPartner);
        setInitialIsRouteToBigWord(isRouteToBigWord);
        setInitialIsRouteToEffectiff(isRouteToEffectiff);
        setHasChanges(false);
        
        let successMessage = `Settings saved successfully`;
        if (appliedToChild && response.data.data?.totalUpdatedCustomers) {
          successMessage += ` (applied to ${response.data.data.totalUpdatedCustomers} customer${response.data.data.totalUpdatedCustomers > 1 ? 's' : ''})`;
        }
        toaster('success', successMessage);
        setShowConfirmModal(false);
      } else {
        throw new Error(response.data.message || 'Failed to update status');
      }
    } catch (err: any) {
      console.error('Failed to update recording/transcription status:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to update status';
      toaster('error', errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle confirmation modal actions
  const handleConfirmSave = (applyToChildren: boolean) => {
    updateRecordingTranscriptionStatus(applyToChildren);
  };

  const handleCancelSave = () => {
    setShowConfirmModal(false);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-10">
        <Spinner animation="border" className="text-gray-500" />
      </div>
    );
  }

  return (
    <div className="vis-mr-root">
      <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-between gap-3 pb-4 mb-4 border-bottom border-gray-200">
        <div>
          <h4 className="fw-bold text-gray-900 mb-1">OPI Settings</h4>
          <p className="text-muted mb-0">
            Configure settings for Over-the-Phone Interpreting (OPI) calls.
          </p>
        </div>
        <Button
          className="vis-mr-btn-primary px-4 py-2 fw-semibold flex-shrink-0"
          onClick={handleSave}
          disabled={!hasChanges || loading || isSaving}
        >
          {isSaving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>

      <div className="card border border-gray-300 overflow-hidden ipi-settings-card">
        <SettingsSection
          title="Call capture"
          description="Recording, transcription, and pre-call prompts for OPI contacts."
          iconClass="bi-telephone"
        >
          <SettingRow
            label="Call Recording"
            description="Enable automatic recording of OPI calls."
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="call-recording-switch"
                className="vis-mr-toggle m-0"
                checked={callRecording}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleRecordingChange(e.target.checked);
                }}
                disabled={loading || isSaving}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          />
          <SettingRow
            label="Call Transcription"
            description={
              callRecording
                ? 'Enable real-time transcription of OPI calls.'
                : 'Requires Call Recording to be enabled.'
            }
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="call-transcription-switch"
                className="vis-mr-toggle m-0"
                checked={callTranscription}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleTranscriptionChange(e.target.checked);
                }}
                disabled={!callRecording || loading || isSaving}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          />
          <SettingRow
            label="Ask Pre-Call Question And Answer"
            description="Enable pre-call questions and answers for OPI calls."
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="ask-pre-call-qa-switch"
                className="vis-mr-toggle m-0"
                checked={askPreCallQA}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setAskPreCallQA(e.target.checked);
                }}
                disabled={loading || isSaving}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          />
          <SettingRow
            label="Gender Preference IVR"
            description="Enable gender preference prompt in OPI IVR flow."
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="gender-preference-ivr-switch"
                className="vis-mr-toggle m-0"
                checked={isGenderIVR}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setIsGenderIVR(e.target.checked);
                }}
                disabled={loading || isSaving}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          />
        </SettingsSection>

        <hr className="m-0 border-gray-200" />

        <SettingsSection
          title="Routing"
          description="Where the call goes when no interpreter accepts."
          iconClass="bi-signpost-split"
        >
          <SettingRow
            label="Forward to Operator"
            description="When enabled, OPI calls are forwarded to an operator if no interpreter accepts. When disabled, calls skip operator routing and continue to the next flow."
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="forward-to-operator-switch"
                className="vis-mr-toggle m-0"
                checked={isForwardToOperator}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setIsForwardToOperator(e.target.checked);
                }}
                disabled={loading || isSaving}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          />
          <SettingRow
            label="Route to Backstop Partner"
            description="When enabled, exhausted OPI calls may route to BigWord and/or Effectiff. When disabled, calls do not route to any backstop partner."
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="route-to-backstop-partner-switch"
                className="vis-mr-toggle m-0"
                checked={isRouteToBackstopPartner}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setIsRouteToBackstopPartner(e.target.checked);
                }}
                disabled={loading || isSaving}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          >
            {isRouteToBackstopPartner && (
              <div className="d-flex flex-wrap align-items-center gap-4">
                <Form.Check
                  type="switch"
                  role="switch"
                  id="route-to-bigword-switch"
                  className="vis-mr-toggle m-0"
                  label="BigWord"
                  checked={isRouteToBigWord}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setIsRouteToBigWord(e.target.checked);
                  }}
                  disabled={loading || isSaving}
                />
                <span className="bi bi-arrow-right"></span>
                <Form.Check
                  type="switch"
                  role="switch"
                  id="route-to-effectiff-switch"
                  className="vis-mr-toggle m-0"
                  label="Effectiff"
                  checked={isRouteToEffectiff}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setIsRouteToEffectiff(e.target.checked);
                  }}
                  disabled={loading || isSaving}
                />
              </div>
            )}
          </SettingRow>
        </SettingsSection>
      </div>

      <Modal show={showConfirmModal} onHide={handleCancelSave} centered>
        <Modal.Header closeButton>
          <Modal.Title>Apply Settings to Child Customers?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Do you want to apply these settings to all child customers as well?</p>
          <p className="text-muted small">
            If yes, the settings will be applied to this customer and all nested child customers recursively.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => handleConfirmSave(false)} disabled={isSaving}>
            No, This Customer Only
          </Button>
          <Button
            className="btn-primary"
            onClick={() => handleConfirmSave(true)}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Yes, Apply to All Children'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
